import { useState, useEffect, useMemo } from 'react'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import ChatPage from 'supportAssistant/pages/ChatPage'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import SocketContextProvider from 'supportAssistant/contexts/SocketContext'
import { useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import { rem } from 'polished'

const Frame = styled.iframe`
  overflow-y: auto;
  min-height: ${rem(1200)};
  -webkit-overflow-scrolling: touch;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(1650)};
  }

  ${mediaQueryUp.desktop} {
    min-height: ${rem(1100)};
  }

  ${mediaQueryUp.largeDesktop} {
    min-height: ${rem(1200)};
  }
`

interface IFrame extends HTMLIFrameElement {
  node: HTMLIFrameElement
}

interface Props {
  supportAssistantEnabled: boolean
}

function SupportPage({
  supportAssistantEnabled,
}: Props) {
  const intl = useIntl()
  const [height, setHeight] = useState(1200)
  const [iframeKey, setIframeKey] = useState(0)
  const iframeRef: React.RefObject<IFrame | null> = React.useRef(null)

  const navigation = useAppNavigation()
  const location = useLocation()
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])

  const isDevMode = queryParams.get('devMode')
  const faq = queryParams.get('faq')

  useEffect(() => {
    // sets random iframeKey when we point to support url without faq query param
    // sets faq query param so when we click on navbar support link we go to /support page without faq query param
    // which will trigger useEffect and set random iframeKey to reset iframe state
    if (!faq && !(supportAssistantEnabled || isDevMode)) {
      setIframeKey(Math.random())
      queryParams.set('faq', '1')
      navigation.replace({
        search: queryParams.toString(),
      })
    }
  }, [faq, navigation, queryParams, supportAssistantEnabled, isDevMode])
  // See https://github.com/lux-group/www-le-customer/pull/4838#discussion_r597384790 for an explanation why we need useEffect above

  useEffect(() => {
    const handleResize = (size: number) => {
      if (size !== 0) {
        setHeight(size)
      }
    }

    const resizeMessageHandler = async(event: MessageEvent<any>) => {
      const data = typeof event?.data === 'string' && JSON.parse(event?.data)
      if (data?.iframeResize) {
        handleResize(data.message)
      }
    }

    window.addEventListener('message', resizeMessageHandler)
    return () => window.removeEventListener('message', resizeMessageHandler)
  }, [])

  if (supportAssistantEnabled || isDevMode) {
    return <SocketContextProvider>
      <ChatPage />
    </SocketContextProvider>
  }

  return (
    <Frame
      title={intl.formatMessage({
        id: 'YVE4UzEEHDz6uy6er8jW',
        defaultMessage: 'Support',
        description: 'Title for embedded support page iframe in the customer support section',
      })}
      frameBorder="0"
      src={config.supportUrl}
      ref={iframeRef}
      height={height + 'px'}
      key={iframeKey}
    />
  )
}

function mapStateToProps(state: App.State) {
  return {
    supportAssistantEnabled: isSupportAssistantEnabled(state),
  }
}

export default connect(mapStateToProps)(SupportPage)
